/**
 * What may still change on a demand in each status (HS2-Y50H8E) — part of the client↔server
 * contract, so every mutating route and the editor's visible controls apply the same lock.
 *
 * Status is derived from data (FR-5.5), but `decided` and `closed` are also locks:
 *
 * - `contents` — line items, quotations, documents and annotations, analysis runs, landed-cost
 *   assumptions, and source matches. Locked once the demand is `decided`, so a decided purchase is
 *   not quietly reshaped underneath its decisions.
 * - `details` — the demand's title, description, needed-by date, and notes. Locked once `closed`
 *   (or `rejected`: every line rejected, HS2-Z6MFSA, which locks like `closed`).
 * - `decisions` — line decisions and purchase outcomes. Still open while `decided` (the purchase
 *   has not happened yet); locked once `closed`.
 *
 * A `cancelled` demand is locked in every area. A reviewer's **Reopen for Changes** unlocks a
 * decided or closed demand until it next reaches `decided` or `closed` again (or they lock it).
 * Role rules (a requester editing only their own draft, staff-only commercial areas) are separate
 * and still apply on top.
 */

export const DEMAND_EDIT_AREAS = ['contents', 'details', 'decisions'] as const;
export type DemandEditArea = (typeof DEMAND_EDIT_AREAS)[number];

/** The statuses whose lock a reviewer can lift with Reopen for Changes. */
export const LOCKABLE_STATUSES = ['decided', 'closed', 'rejected'] as const;

/** Whether `status` is decided or closed, the states Reopen for Changes applies to. */
export function isLockableStatus(status: string): boolean {
  return (LOCKABLE_STATUSES as readonly string[]).includes(status);
}

/** Whether `area` of a demand in `status` may change; `reopenedForChanges` lifts a decided/closed lock. */
export function demandAreaEditable(status: string, area: DemandEditArea, reopenedForChanges = false): boolean {
  if (status === 'cancelled') return false;
  if (reopenedForChanges && isLockableStatus(status)) return true;
  if (status === 'closed' || status === 'rejected') return false;
  if (status === 'decided') return area !== 'contents';
  return true;
}

/** The user-facing reason an area is locked, for a 409 body; null when it is editable. */
export function demandLockReason(status: string, area: DemandEditArea, reopenedForChanges = false): string | null {
  if (demandAreaEditable(status, area, reopenedForChanges)) return null;
  if (status === 'cancelled') return 'demand is cancelled; reopen it to make changes';
  if (status === 'closed') return 'demand is complete; reopen it for changes first';
  if (status === 'rejected') return 'demand is rejected; reopen it with an update first';
  return 'demand is decided; reopen it for changes to edit lines, quotes, or documents';
}
